import { Typography } from "@/components/common/Typography/Typography"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useCallback, useState } from "react"

const NOTE_MAX_LENGTH = 1000
// The counter only earns its keep near the limit; below this it would be
// one more number nobody needs to see.
const NOTE_COUNTER_THRESHOLD = 900

export interface DataRoomNoteEditorProps {
  /** The file's name, used only to give the textarea an accessible name. */
  fileName: string
  /** What the editor opens with: a kept draft if there is one, else the
   * saved note, else "". */
  initialValue: string
  /** The note as saved on the server; Save stays disabled while the text
   * still equals it. Defaults to `initialValue`. */
  savedValue?: string
  onSave: (note: string) => void
  onCancel: () => void
  /** Reports every keystroke so the page can keep a draft alive across
   * folder navigation; the editor itself stays uncontrolled. */
  onChange?: (value: string) => void
  /** True while the save mutation is in flight; disables Save. */
  isSaving: boolean
}

/** Inline note editor rendered under a data room file row. Cmd/Ctrl+Enter
 * saves, Esc cancels — same shortcuts as every other inline editor here. */
export const DataRoomNoteEditor: React.FC<DataRoomNoteEditorProps> = ({
  fileName,
  initialValue,
  savedValue,
  onSave,
  onCancel,
  onChange,
  isSaving,
}) => {
  const { translate } = useTranslation()
  const [value, setValue] = useState(initialValue)
  const [baseline] = useState(savedValue ?? initialValue)
  const isUnchanged = value === baseline

  const handleSave = useCallback(() => {
    if (isSaving || isUnchanged) return
    onSave(value)
  }, [isSaving, isUnchanged, value, onSave])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        event.preventDefault()
        handleSave()
      } else if (event.key === "Escape") {
        event.preventDefault()
        onCancel()
      }
    },
    [handleSave, onCancel],
  )

  return (
    <div
      className="mb-3.5 ml-9 flex max-w-2xl flex-col gap-2"
      data-testid="data-room-note-editor"
    >
      <Textarea
        value={value}
        onChange={(event) => {
          setValue(event.target.value)
          onChange?.(event.target.value)
        }}
        onKeyDown={handleKeyDown}
        maxLength={NOTE_MAX_LENGTH}
        autoFocus
        placeholder={translate("DATA_ROOM/NOTE_PLACEHOLDER")}
        aria-label={translate("DATA_ROOM/NOTE_ARIA", { name: fileName })}
        className="min-h-[72px] resize-none text-sm"
      />
      <div className="flex items-center justify-between gap-3">
        <Typography variant="textXS" className="text-grey5">
          {value.length >= NOTE_COUNTER_THRESHOLD
            ? translate("DATA_ROOM/NOTE_COUNTER", { count: value.length })
            : translate("DATA_ROOM/NOTE_AUDIENCE")}
        </Typography>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            {translate("DATA_ROOM/NOTE_CANCEL")}
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleSave}
            disabled={isSaving || isUnchanged}
          >
            {translate("DATA_ROOM/NOTE_SAVE")}
          </Button>
        </div>
      </div>
    </div>
  )
}
